/* eslint-disable max-len */
/**
 * @author Madhuri <sai.madhuri@314ecorp.com>
 * @description faq question-answers
 */

import _ from 'lodash';

import { reactNodeToString } from 'components/widgets/TextHighlighter';
import { getSupportEmail } from 'components/widgets/DetailsRenderer';
import { FAQ_CATEGORIES } from 'configs/index';

const claimStatusCodes = () => {
	const claimStatusCodesLink = 'https://x12.org/codes/claim-status-codes';
	return (
		<a href={claimStatusCodesLink}>
			<u>{claimStatusCodesLink}</u>
		</a>
	);
};

const CODE_EXPLORER_TUTORIAL_VIDEO =
	'https://res.cloudinary.com/abegerho/video/upload/v1751521081/veritable-website/Veritable_Code_Explorer_1_e41c49a86c.mp4';

const getAllQueries = (email: string, serviceTypes: EligibilityRequestForm.IServiceType[]): Faq.ISchema[] => {
	const allQueries: Faq.ISchema[] = [
		{
			key: 'o1',
			category: [FAQ_CATEGORIES.Onboarding.category],
			question: 'My onboarding process failed midway. What should I do?',
			answer: (
				<p>
					{
						'If your onboarding has failed midway, you will be automatically directed to the first screen of the onboarding process. Retry the process from the beginning. Please ensure you are proceeding with your provider verification correctly. The provider whose NPI is being submitted should be the provider who is completing the electronic identity verification.'
					}
					<br />
					{'However, if the issue persists, please contact us at'}
					{getSupportEmail({ email })}
					{'.'}
				</p>
			),
		},
		{
			key: 'o2',
			category: [FAQ_CATEGORIES.Onboarding.category],
			question: 'I am unable to complete my onboarding. How can I contact Veritable"s customer support for assistance?',
			answer: (
				<p>
					{'You may contact us at'}
					{getSupportEmail({ email })}
					{'. We will get back to you within 24 hours.'}
				</p>
			),
		},
		{
			key: 'o3',
			category: [FAQ_CATEGORIES.Onboarding.category],
			question: 'Which NPI am I supposed to provide during the onboarding process?',
			answer: (
				<p>
					{'During onboarding, you can provide the NPI of either of the following:'}
					<br />
					{
						<ul>
							<li>
								{
									'You can enter a Type 1 NPI, which is associated with an individual provider who is part of your organization.'
								}
							</li>
							<li>
								{
									'You can enter a Type 2 NPI, which is the NPI of your organization. The person registered as the authorized official of your organization will be recognized, and the process ahead will be continued by them.'
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'o4',
			category: [FAQ_CATEGORIES.Onboarding.category],
			question: 'What is the electronic verification of the provider?',
			answer: (
				<p>
					{
						'After submitting the NPI, you will now be required to verify your identity. Please ensure that the provider/authorized official whose NPI has been submitted is the same person who is proceeding with electronic identity verification.'
					}
					<br />
					{'The verification is a two-step process:'}
					<br />
					{
						<ul>
							<li>{'Verification using a government-issued ID such as a driver"s license or passport'}</li>
							<li>
								{
									'Virtual identity verification of the provider/authorized official via laptop webcam or mobile phone camera.'
								}
							</li>
						</ul>
					}
					{
						'Both steps are completed online within the Veritable Portal. Please be assured that we do not collect or store any sensitive information in the process.'
					}
				</p>
			),
		},
		{
			key: 'o5',
			category: [FAQ_CATEGORIES.Onboarding.category],
			question: 'What data format does your software currently use?',
			answer: (
				<p>
					{
						"We're happy to announce that our software is now FHIR native. This means we seamlessly exchange healthcare data using the Fast Healthcare Interoperability Resources (FHIR) standard, and this allows for more efficient and standardized data exchange. "
					}
					<br />
					{'In addition to viewing the data in the UI, we also provide data in FHIR, X12 and JSON formats. '}
				</p>
			),
		},
		{
			key: 'pr1',
			category: [FAQ_CATEGORIES.Provider.category],
			question: 'How do I add Providers for eligibility verification and claim status checks?',
			answer: (
				<p>
					{'To add providers to the system:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Providers' section."}</li>
							<li>{"Click the 'Add Provider' button located in the top-right corner."}</li>
							<li>
								{
									"Enter the 'NPI Number'. The system will automatically pull the provider's name from the NPI registry. You can edit the name if needed."
								}
							</li>
							<li>{'Click Save.'}</li>
							<li>{"After saving, you will see the option to 'Verify Identity'."}</li>
							<li>{"Click 'Verify Identity' to initiate the electronic verification process through a Stripe modal."}</li>
							<li>
								{
									"Once the provider's identity is successfully verified, they will be added and activated for eligibility and claim status checks."
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pr2',
			category: [FAQ_CATEGORIES.Provider.category],
			question: 'How do I enroll a provider with a payer that requires provider-payer enrollment?',
			answer: (
				<p>
					{'To enroll a provider with a payer:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Payers' section."}</li>
							<li>{"Click the 'Add Payer' button in the top-right corner."}</li>
							<li>
								{
									"Search for a payer or browse through the list in the drawer. Payers that require enrollment will have an 'Enrollment Required' tag next to their names."
								}
							</li>
							<li>
								{
									"Click the 'Add' button. You will be redirected to the Configure screen. (If the payer was already added to your system, you can directly search for the payer in the table and click the 'Configure payer' icon in the 'Actions' column.)"
								}
							</li>
							<li>{'Find the provider name you want to enroll.'}</li>
							<li>{"Click 'Request Enrollment' next to the 'Enrollment Status' column to initiate enrollment."}</li>
							<li>{'In the modal that appears, enter the TIN/EIN.'}</li>
							<li>{'Click "Enroll" to initiate the enrollment process.'}</li>
						</ul>
					}
					{<b>{'Note: '}</b>}
					{'The enrollment process typically takes 2–3 business days to complete.'}
				</p>
			),
		},
		{
			key: 'pr3',
			category: [FAQ_CATEGORIES.Provider.category],
			question: 'How do I verify the identity of a provider?',
			answer: (
				<p>
					{'To verify a provider"s identity:'}
					<br />
					{
						<ul>
							<li>
								{
									"Click the 'Settings' icon in the top panel and navigate to the 'Providers' section. Find the provider you want to verify."
								}
							</li>
							<li>
								{
									"Click 'Verification Required' in the 'Identity Verification Status' column OR click the 'Edit' from the 'Actions' column to go to the detailed view."
								}
							</li>
							<li>{'Click "Verify Identity" to initiate the electronic verification process through a Stripe modal.'}</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pr4',
			category: [FAQ_CATEGORIES.Provider.category],
			question: 'How do I deactivate a provider?',
			answer: (
				<p>
					{'To deactivate a provider within the system:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Providers' section."}</li>
							<li>{'Find the provider you want to deactivate.'}</li>
							<li>{"Disable the provider using the 'Status' toggle and click on 'Save'."}</li>
							{<b>{'Note: '}</b>}
							{
								'Once disabled, providers will be unable to perform eligibility verifications or check claim statuses. To re-enable providers, click on “Enable” in the actions column or click on “Edit” and enable the provider in the “Status” toggle.'
							}
							<li>{'To re-activate the provider, change the "Status" toggle and click on "Save."'}</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pa1',
			category: [FAQ_CATEGORIES.Payer.category],
			question: 'How do I add payers for eligibility verification and claim status checks?',
			answer: (
				<p>
					{'To add payers to the system:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Payers' section."}</li>
							<li>{'Search for a payer or browse through the list in the table.'}</li>
							<li>
								{
									"The 'Eligibility' and 'Claim Status' tags under the 'Transactions' column indicate whether the payer allows for eligibility and benefits verification, claim status checks, or both"
								}
							</li>
							<li>{"Click the '+' (Add) icon in the 'Actions' column next to the payer to add a payer."}</li>
							<li>
								{
									"In case the payer requires provider enrollment, you will be redirected to the 'Configure' screen. Check out Question 2 for further information."
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pa2',
			category: [FAQ_CATEGORIES.Payer.category],
			question: 'How do I add payers that require provider-payer enrollment?',
			answer: (
				<p>
					{'To add payers that require provider-payer enrollment:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Payers' section."}</li>
							<li>
								{
									"Search for a payer or browse through the list in the table. Payers that require enrollment will have a 'Yes' tag under the Enrollment Required column."
								}
							</li>
							<li>{'You can also apply the Enrollment Required filter at the top to filter out the payers'}</li>
							<li>
								{
									"Click the '+' (Add) icon in the 'Actions' column next to the payer. You will be redirected to the Configure screen. (If the payer was already added to your system, you can directly search for the payer in the table and click the 'Configure payer' icon in the 'Actions' column.)"
								}
							</li>
							<li>{'Find the provider name you want to enroll.'}</li>
							<li>{"Click 'Request Enrollment' next to the 'Enrollment Status' column to initiate enrollment."}</li>
							<li>{'In the modal that appears, enter the TIN/EIN.'}</li>
							<li>{'Click "Enroll" to initiate the enrollment process.'}</li>
						</ul>
					}
					{<b>{'Note: '}</b>}
					{'The enrollment process typically takes 2–3 business days to complete.'}
				</p>
			),
		},
		{
			key: 'pa3',
			category: [FAQ_CATEGORIES.Payer.category],
			question: 'Is there a limit to the number of payers that I can add?',
			answer: <p>{'No, there is no limit to the number of payers you can add. '}</p>,
		},
		{
			key: 'pa4',
			category: [FAQ_CATEGORIES.Payer.category],
			question: 'How do I check my enrollment status with a payer?',
			answer: (
				<p>
					{'To check your enrollment status with a payer:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Payers' section."}</li>
							<li>{"Search for the payer in the table and click the 'Configure payer' icon in the 'Actions' column."}</li>
							<li>
								{
									"Check the 'Enrollment Status' column for each provider's enrollment status. You may see one of the following statuses:"
								}
								<ol type='a'>
									<li>
										{<b>{'Requested: '}</b>}
										{'Your request is still under process by the payer.'}
									</li>
									<li>
										{<b>{'Enrolled: '}</b>}
										{'The enrollment is complete and the provider has been successfully enrolled with the payer.'}
									</li>
									<li>
										{<b>{'Rejected: '}</b>}
										{'The enrollment has failed. Please check with the payer for more details.'}
									</li>
									<li>
										{<b>{'Not Enrolled: '}</b>}
										{
											"You have not yet requested enrollment. Click on 'Request Enrollment' in the next column to begin the process."
										}
									</li>
								</ol>
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pa5',
			category: [FAQ_CATEGORIES.Payer.category],
			question:
				'After adding a payer, I don"t see it in the payer list in the eligibility/claim status check request forms. What could be the possible reasons?',
			answer: (
				<p>
					{'You may not see a payer in the request screens due to the following reasons:'}
					<br />
					{
						<ul>
							<li>
								{
									'If the payer allows only eligibility requests, it will not appear in the claim status check request form.'
								}
								<br />
								{
									"You can check the allowed transactions for a payer in the 'Payers' section by looking at the 'eligibility' or 'claim status' tags under the 'Transactions' column."
								}
							</li>
							<li>
								{
									'Similarly, if the payer allows only claim status checks, it will not appear in the eligibility request form.'
								}
							</li>
							<li>
								{'The payer may require enrollment.'}
								<br />
								{
									'If the provider is not enrolled with the payer, the payer will appear grayed out in the eligibility or claim status check request form.'
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pa6',
			category: [FAQ_CATEGORIES.Payer.category],
			question: 'I did not disable/remove a payer from my list of payers. Why is it still not visible anymore?',
			answer: (
				<p>
					{'Payers may not be visible for eligibility or claim status checks if:'}
					<br />
					{
						<ul>
							<li>
								{'They could have been removed from our network of payers.'}
								<br />
								{
									'In such cases, the payers are archived in our system. You can still view any previous calls that you may have made to the payer, but you would no longer be able to submit any new requests to them.'
								}
							</li>
							<li>
								{'Someone from your organization could have deactivated the payer.'}
								<ol type='a'>
									<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Payers' section."}</li>
									<li>
										{
											"Search for the payer in the table. If you the payer has not been added, add the payer again by clicking the '+' (Add) icon in the 'Actions' column next to the payer."
										}
									</li>
									<li>
										{
											"If you find the payer in the table, check if the payer status is marked as 'Inactive'. If yes, click the 'Activate' icon in the 'Actions' column."
										}
									</li>
								</ol>
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'pa7',
			category: [FAQ_CATEGORIES.Payer.category],
			question: 'How do I deactivate/remove payers from my Eligibility and Claim Status request screen dropdowns?',
			answer: (
				<p>
					{'To deactivate payers from Eligibility or Claim Status requests:'}
					<br />
					{
						<ul>
							<li>{"Click the 'Settings' icon in the top panel and navigate to the 'Payers' section."}</li>
							<li>{"Search for the payer in the table and click the 'Configure payer' icon in the 'Actions' column."}</li>
							<li>{'Click the 3 dotted menu at the top-right corner and select the "Deactivate Payer" option.'}</li>
							<li>{'In the modal that appears, confirm the deactivation. '}</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'e1',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I submit eligibility verification requests? Is batch verification possible?',
			answer: (
				<p>
					{'You can submit eligibility verification requests in two ways:'}
					<br />
					{
						<ul>
							<li>
								{<b>{'Single patient request: '}</b>}
								{
									'Go to the "Eligibility" tab, click the "New Request" button, and fill in the required details to initiate a request. Check out Question 2 for further information.'
								}
							</li>
							<li>
								{<b>{'Batch upload: '}</b>}
								{
									'Go to the "Eligibility" tab, click the "New Batch" button, and follow the steps to initiate a batch verification. Once completed, proceed to run the batch immediately. Check out Questions 11-25 for further information regarding the batch upload process.'
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'e2',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I submit a new eligibility verification request for a single patient?',
			answer: (
				<p>
					{'To submit a new eligibility verification request for a single patient:'}
					<br />
					{
						<ul>
							<li>{'Go to the "Eligibility" tab - it will take you directly to the "Responses" page.'}</li>
							<li>{'Click the "New Request" button located in the top-right corner.'}</li>
							<li>{'Fill in the required details in the request form.'}</li>
							<li>{'Click "Submit" to initiate the request.'}</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'e3',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I edit my eligibility request?',
			answer: (
				<p>
					{'To revise a previously submitted eligibility request:'}
					<br />
					{
						<ul>
							<li>{'Open the detailed response view of the eligibility request.'}</li>
							<li>{'Click the "Edit Request" icon located in the top-right corner.'}</li>
							<li>
								{
									'Alternatively, on the "Responses" page, locate the eligibility request in the table, and click the "Edit Request" icon under the Actions column.'
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'e4',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I view my previous requests for eligibility and benefits verification? ',
			answer: (
				<p>
					{
						'To view your previous requests, go to the "Responses" screen under the "Eligibility" tab. You can look at your most recent requests in the table or simply search for a particular request.'
					}
				</p>
			),
		},
		{
			key: 'e5',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What kind of information will I receive in the eligibility response from the payer?',
			answer: (
				<p>
					{'A typical eligibility response includes:'}
					<br />
					{
						<ul>
							<li>
								{<b>{'Patient information: '}</b>}
								{'Name, member ID, gender, relationship to the subscriber, etc.'}
							</li>
							<li>
								{<b>{'Plan details: '}</b>}
								{'Plan name and type, coverage start and end dates.'}
							</li>
							<li>
								{<b>{'Benefits and coverage: '}</b>}
								{
									'Eligibility for a bundle of services such as physician visits, emergency care, hospital visits, and others services that the payer might include as part of Health Benefit Plan Coverage.'
								}
							</li>
							<li>
								{<b>{'Patient responsibility: '}</b>}
								{
									'Deductibles (total and remaining), copayments, coinsurance, along with limitations, out-of-pocket maximums, and coverage levels (individual, family, employee, etc.). Depending on the plan, benefit details are also returned for various provider networks.'
								}
							</li>
							<li>
								{<b>{'Other details: '}</b>}
								{'Primary care provider (PCP) information and whether the insurance is primary or secondary.'}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'e6',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I export the list of previous eligibility requests? ',
			answer: (
				<p>
					{'To export your previous eligibility requests:'}
					<br />
					<ul>
						<li>{'Go to the "Responses" page.'}</li>
						<li>{'Click the "Download Responses" icon located right above the table.'}</li>
						<li>{'In the modal that appears, select the desired time frame.'}</li>
						<li>{'Click "Download Responses" to initiate the download.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e7',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What are the different errors I may face after submitting a valid eligibility verification request?',
			answer: (
				<p>
					{'You may face the following errors after submitting a valid verification request:'}
					<br />
					{
						<ul>
							<li>
								{<b>{'Input Error: '}</b>}
								{
									'Occurs when the payer fails to recognize one or more submitted details about the patient. The error message usually indicates which part of the input is incorrect or missing. For example, a message like "Member Not Found" may suggest that the entered member ID is incorrect.'
								}
							</li>
							<li>
								{<b>{'Network Error: '}</b>}
								{
									'Indicates downtime, either from the payer"s side or ours. You can resubmit the request after some time. If the error persists, please contact us at'
								}
								{getSupportEmail({ email })}
								{'.'}
							</li>
							<li>
								{<b>{'Implementation Error: '}</b>}
								{
									'Occurs when a request is successfully submitted but does not return patient eligibility details, usually due to unknown reasons. You can try resubmitting the request after some time. If the error persists, please contact us at'
								}
								{getSupportEmail({ email })}
								{'.'}
							</li>
							<li>
								{<b>{'No Response Error: '}</b>}
								{
									"Occurs when the claim status check request is successfully sent to the payer, but the payer does not return any response. This typically means the request reached the payer's system, but no data was sent back, often due to unknown or temporary issues on the payer's end."
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'e8',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'Which service types are supported for an eligibility verification request?',
			answer: (
				<p>
					<ul>
						<li>
							{
								'You can check your patient"s eligibility and benefits for over 200 service types. These include general plan coverage as well as specific services such as Durable Medical Equipment (DME) Used, DME Rental, Consultation, Pathology, and more.'
							}
						</li>
						<li>
							{
								'However, some payers may not support specific service types. In such cases, they may return a standard response for'
							}
							<b>{' Health Benefit Plan Coverage (30) '}</b>
							{'regardless of the service type submitted.'}
						</li>
						<li>
							{<b>{'Health Benefit Plan Coverage '}</b>}
							{
								'typically includes general information about the plan along with bundled details for commonly requested services like physician visits and emergency services.'
							}
						</li>
						<li>
							{
								'While you can query multiple service types in a single eligibility request, not all payers support multi-service queries. If the payer does support it, the system will return all available responses.'
							}
						</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e9',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What are the actual medical service types Veritable supports?',
			answer: (
				<div>
					{_.map(serviceTypes, (serviceType) => (
						<span className='block'>{`"${serviceType?.code}": "${serviceType?.name}"`}</span>
					))}
				</div>
			),
		},
		{
			key: 'e10',
			question: 'What is considered as a transaction?',
			category: [FAQ_CATEGORIES.Eligibility.category],
			answer: 'Each individual eligibility verification request or a claim status check request is considered as one transaction regardless of the mode of input (single patient or batch upload).',
		},
		{
			key: 'e11',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I submit a Batch Upload request?',
			answer: (
				<p>
					{'To submit a Batch Upload request:'}
					<br />
					<ul>
						<li>{'Go to the "Eligibility" tab - it will take you directly to the "Responses" page.'}</li>
						<li>{'Click the "New Batch" button located in the top-right corner. '}</li>
						<li>{'Download the batch template with pre-set headers or upload your own CSV file.'}</li>
						<li>{'Complete the header mapping process.'}</li>
						<li>{'Validate your file and map your providers and payers.'}</li>
						<li>{'Proceed to run the batch immediately.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e12',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I identify the required fields for my batch file?',
			answer: 'Click the "View the mandatory fields" to see a list of all required and optional fields needed for successful batch file processing.',
		},
		{
			key: 'e13',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'Do I need to rename the headers in my file to match the system?',
			answer: "No. After uploading your file, you'll be guided through the header mapping process where you can map your file’s headers to the corresponding Veritable fields.",
		},
		{
			key: 'e14',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How does the header mapping process work?',
			answer: (
				<p>
					{'After uploading your file:'}
					<br />
					<ul>
						<li>{'You"ll be prompted to map your file"s headers to Veritable"s fields'}</li>
						<li>{'Select the appropriate header for each Veritable field using the dropdowns.'}</li>
						<li>{'Once mapped, the system remembers your previous mappings to make future uploads faster.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e15',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What should I do if my file contains extra columns?',
			answer: 'Extra columns not needed for processing can be ignored. You only need to map the fields required by Veritable.',
		},
		{
			key: 'e16',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What happens if my uploaded file contains errors?',
			answer: 'If errors are detected during validation, you"ll receive a downloadable file with the issues highlighted. You can correct the errors and re-upload the file, or choose to skip the problematic entries and proceed with the rest.',
		},
		{
			key: 'e17',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How do I map my provider and payer data during Batch Upload?',
			answer: (
				<ul>
					<li>{'After completing "Header Mapping", the "Data Mapping" interface will appear.'}</li>
					<li>{'Map your Provider names, Payers, Service Types (optional), and Locations (optional).'}</li>
					<li>{'Review and confirm the system-suggested mappings before proceeding further.'}</li>
				</ul>
			),
		},
		{
			key: 'e18',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What does the "Provider Enrollment Required" pop-up mean during payer mapping?',
			answer: (
				<p>
					{
						'Some payers require provider enrollment before performing eligibility checks. When this pop-up appears during mapping, you have two options: '
					}
					<ul>
						<li>{'Request enrollment for the providers (typically completed within 2–3 business days), or'}</li>
						<li>{'Skip those providers and proceed with the ones already enrolled.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e19',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'Does the system save my mapping preferences for future uploads?',
			answer: 'Yes. The system automatically saves your mapping preferences from your last submission and applies them during future uploads.',
		},
		{
			key: 'e20',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'Can I pause the mapping process and complete it later?',
			answer: 'Yes. You can exit the batch upload screen at any time, and your progress will be saved as a draft. To resume, go to the "Batches" tab in the "Responses" screen and open your saved draft.',
		},
		{
			key: 'e21',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How can I delete or cancel a Batch Upload?',
			answer: (
				<p>
					{'Yes. You can: '}
					<ul>
						<li>{'Cancel during mapping using the option at the top of the mapping screen'}</li>
						<li>{'Delete in-progress requests from the "Batches" page under "Eligibility"'}</li>
					</ul>
				</p>
			),
		},
		// {
		// 	key: 'e22',
		// 	category: [FAQ_CATEGORIES.Eligibility.category],
		// 	question: 'Can I schedule the batch eligibility checks for a later time?',
		// 	answer: 'Yes. You can either run the eligibility checks immediately or schedule them for a later time.',
		// },
		{
			key: 'e23',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'How am I notified when eligibility checks are complete?',
			answer: 'You will receive a notification email once the checks are complete. You can also monitor real-time progress on the "Responses" screen under the "Eligibility" tab by toggling to the "Batch" view.',
		},
		{
			key: 'e24',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'Where can I access the results of my batch upload?',
			answer: (
				<p>
					{'To access batch upload results: '}
					<br />
					<ul>
						<li>{'Go to the "Eligibility" tab and click on "Responses"'}</li>
						<li>{'Toggle to the "Batch" view'}</li>
						<li>{'Click on a specific batch number to view details'}</li>
						<li>{'View individual responses listed in the table below'}</li>
						<li>
							{
								'Download results by clicking the download icon (↓) next to "Processed," "Failed," or "Unprocessed" categories'
							}
						</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e25',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What do the Processed, Failed, and Unprocessed categories mean in my batch results?',
			answer: (
				<p>
					{'The batch results are categorized as follows: '}
					<br />
					<ul>
						<li>
							{'Processed: Transactions that were successfully processed (includes Active, Inactive, and Unknown statuses)'}
						</li>
						<li>{'Failed: Transactions that encountered errors during processing'}</li>
						<li>
							{
								'Unprocessed: Transactions that were intentionally unprocessed due to multiple errors associated with a specific payer.'
							}
						</li>
					</ul>
				</p>
			),
		},
		{
			key: 'e26',
			category: [FAQ_CATEGORIES.Eligibility.category],
			question: 'What file formats are available for downloading responses?',
			answer: (
				<p>
					{
						'You can download responses in X12, FHIR, or a custom format of your choice. To enable this feature, contact our support team at'
					}
					{getSupportEmail({ email })}
					{'.'}
				</p>
			),
		},
		{
			key: 'c1',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'How do I submit a new claim status check request?',
			answer: (
				<p>
					{'To submit a new claim status check request:'}
					<br />
					{
						<ul>
							<li>{'Go to the "Claims" tab - it will take you directly to the "All Claims" page.'}</li>
							<li>{'Click the "Check Claim Status" button located in the top-right corner. '}</li>
							<li>{'Fill out the required details in the request form.'}</li>
							<li>{'Click "Submit" to initiate the request.'}</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'c2',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'How do I edit a claim status check request?',
			answer: (
				<p>
					{'To revise a previously submitted claim status check request:'}
					<br />
					{
						<ul>
							<li>{'Open the detailed response view of the claim.'}</li>
							<li>{"From the menu on the right, click on the 'Edit Claim Status Check' option. "}</li>
							<li>
								{
									'Alternatively, on the "All Claims" page, locate the claim in the table, and click the "Edit Request" icon under the Actions column.'
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'c3',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'How do I view my previous claim checks?',
			answer: (
				<p>
					{
						"To view your previous claim status checks, go to the 'Claims' screen under the 'Claims' tab. You can view your most recent requests in the table or search for a specific request."
					}
				</p>
			),
		},
		{
			key: 'c4',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'What kind of information will I receive in the claim status response from the payer?',
			answer: (
				<p>
					{'A typical claim status response includes:'}
					<br />
					{
						<ul>
							<li>
								{<b>{'Patient information: '}</b>}
								{'Name, member ID (as per payer records), and the account number.'}
							</li>
							<li>
								{<b>{'Claim details: '}</b>}
								{'Claim number, claim status and category, billed Amount, and the paid amount.'}
							</li>
							<li>
								{<b>{'For Processed Claims: '}</b>}
								{'Finalized date and the line-level breakup of various procedures submitted in the claim.'}
							</li>
							<li>
								{<b>{'For Paid Claims: '}</b>}
								{'Check/EFT date, number, and final amount paid.'}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'c5',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'What is the meaning of Claim Age?',
			answer: 'Claim Age refers to the time elapsed between the end date of service for a claim and the current date.',
		},
		{
			key: 'c6',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'What are the different statuses I may receive from the payer for a claim status request?',
			answer: (
				<p>
					{
						'Payers return standard X12 claim status and categories for a claim. You can view the full list of all possible statuses here: '
					}
					{claimStatusCodes()}
					{'.'}
				</p>
			),
		},
		{
			key: 'c7',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'How do I export my previous claim status checks?',
			answer: (
				<p>
					{'To export your previous claim status checks:'}
					<br />
					<ul>
						<li>{'Go to the "All Claims" page.'}</li>
						<li>{'Click the "Download Responses" icon located right above the table.'}</li>
						<li>{'In the modal that appears, select the desired time frame.'}</li>
						<li>{'Click "Download Responses" to initiate the download.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'c8',
			category: [FAQ_CATEGORIES.ClaimStatus.category],
			question: 'What are the different errors I may face after submitting a valid claim status request?',
			answer: (
				<p>
					{'You may encounter the following errors after submitting a valid claim status check request::'}
					<br />
					{
						<ul>
							<li>
								{<b>{'Input Error: '}</b>}
								{
									'Occurs when the payer fails to recognize one or more submitted details about the patient or claim. The error message usually indicates which part of the input is incorrect or missing. For example, a message like "Member Not Found" may suggest that the entered member ID is incorrect.'
								}
							</li>
							<li>
								{<b>{'Network Error: '}</b>}
								{
									'Indicates downtime, either from the payer"s side or ours. You can resubmit the request after some time. If the error persists, please contact us at'
								}
								{getSupportEmail({ email })}
								{'.'}
							</li>
							<li>
								{<b>{'Implementation Error: '}</b>}
								{
									'Occurs when a request is successfully submitted but does not return claim status details, usually due to unknown reasons. You can try resubmitting the request after some time. If the error persists, please contact our support team.'
								}
							</li>
							<li>
								{<b>{'No Response Error: '}</b>}
								{
									"Occurs when the claim status check request is successfully sent to the payer, but the payer does not return any response. This typically means the request reached the payer's system, but no data was sent back, often due to unknown or temporary issues on the payer's end."
								}
							</li>
						</ul>
					}
				</p>
			),
		},
		{
			key: 'ce1',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'How do I get started with Code Explorer?',
			video: CODE_EXPLORER_TUTORIAL_VIDEO,
			answer: <p>{'Here"s a quick walkthrough to get you started:'}</p>,
		},
		{
			key: 'ce2',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'What is Code Explorer?',
			answer: (
				<p>
					{
						'Code Explorer is a medical code search and reference tool built for healthcare professionals. It supports real-time lookup of ICD-10-CM, ICD-10-PCS, CPT, HCPCS and Legacy ICD-9 codes and modifiers. It helps you explore the full official description and related details, and lets you personalize your experience with notes and favorites.'
					}
				</p>
			),
		},
		{
			key: 'ce3',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'How do I search for a code?',
			answer: (
				<p>
					{'To search for a code: '}
					<ul>
						<li>{'Go to the Code Explorer tab on your dashboard.'}</li>
						<li>{'Type a keyword (e.g., “diabetes”) or code (e.g., E11.9) in the search bar.'}</li>
						<li>{'As you type, suggestions will appear in real-time.'}</li>
						<li>{'You can click on a suggested code to go directly to its detailed view.'}</li>
						<li>
							{
								"Alternatively, press Enter to browse all search results and select the code you're looking for to access its full description."
							}
						</li>
					</ul>
				</p>
			),
		},
		{
			key: 'ce4',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'Can I search using both keywords and exact codes?',
			answer: (
				<p>
					{'Yes. You can search using: '}
					<ul>
						<li>{'Full code (e.g., E0607, A23.11, 0F140Z1)'}</li>
						<li>{'Partial code (e.g., A445)'}</li>
						<li>{'Descriptive keywords (e.g., “blood glucose monitor”)'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'ce5',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'How do I favorite a code for quick access later?',
			answer: (
				<p>
					{'To add a code to your favorites: '}
					<ul>
						<li>{'Search for the code.'}</li>
						<li>
							{
								'In the results view, click the "star" icon next to the code name, or you can navigate to the detailed code view and click the "star" icon next to the code name.'
							}
						</li>
						<li>{'To view all your favorites, go to the “Favorites” section in Code Explorer.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'ce6',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'What are internal notes, and how can I add one?',
			answer: (
				<p>
					{
						'Internal notes let you attach personal notes to any code for future reference. They"re helpful for capturing reminders, special coding instructions, or clarifications.'
					}
					<br />
					{'To add a note:'}
					<ul>
						<li>{'Search for the code.'}</li>
						<li>{'In the search results view, click the note icon next to the code.'}</li>
						<li>{'Alternatively, open the code"s detailed view and go to the "Internal Notes" tab.'}</li>
						<li>{'A pop-up will appear where you can write and save your note.'}</li>
						<li>{'Enter your comments or references. You can edit or delete notes anytime.'}</li>
						<li>{'You"ll also see who last edited the note, along with a timestamp for when the edit was made.'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'ce7',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'What is the "View Code Hierarchy" feature?',
			answer: (
				<p>
					{'For ICD-10-CM codes, clicking on "View Code Hierarchy" allows you to:'}
					<ul>
						<li>{'View the related codes'}</li>
						<li>{'Explore code families and categories'}</li>
						<li>{'Understand where the current code fits in the broader classification'}</li>
					</ul>
				</p>
			),
		},
		{
			key: 'ce8',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'How often is the data updated, and what happens if a code is deleted?',
			answer: (
				<p>
					{
						'We update Code Explorer regularly with the latest data from official CMS, AMA, and other coding releases. This ensures you\'re always searching the most accurate and current codes. If a code is deleted, it will appear grayed out with a "Deleted" label so you can avoid using outdated codes in your workflows.'
					}
				</p>
			),
		},
		{
			key: 'ce9',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'What do the icons next to codes mean?',
			answer: (
				<p>
					{
						'Each codeset includes a visual legend to help you quickly understand the icons shown next to each code. These icons indicate additional information for a code like whether the code is new, revised, or has applicable modifiers. You can click on the legend at the bottom of a code"s detailed view to see what each icon represents.'
					}
				</p>
			),
		},
		{
			key: 'ce10',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'Can I copy a code?',
			answer: <p>{'Yes. Use the copy button available on each code card or detail view to copy the code to your clipboard.'}</p>,
		},
		{
			key: 'ce11',
			category: [FAQ_CATEGORIES.CodeExplorer.category],
			question: 'Is there a limit to the number of code searches I can perform?',
			answer: (
				<p>
					{
						'No. You can perform unlimited code searches across all supported code sets—ICD-10-CM, ICD-10-PCS, HCPCS, and CPT. There are no search caps or usage restrictions.'
					}
				</p>
			),
		},
		// {
		// 	key: 'a1',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: ` What is the cost of subscribing to the Payments Add-on? Is there a limit to the number of transactions I can process monthly?`,
		// 	answer: (
		// 		<p>
		// 			{`You can subscribe to the Payments Add-on at a cost of $30/month. You will be charged for the payment add-on on your next Veritable plan renewal date. There is no limit to the number of monthly payment transactions you can process.`}
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a2',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `How can I subscribe to the Payments Add-on?`,
		// 	answer: (
		// 		<ul>
		// 			<li>
		// 				{`You can subscribe to the payments add-on at the time of signing up. During onboarding, you can subscribe to “Payments Add-on” once you have added one or more payers to your Veritable portal.`}
		// 			</li>
		// 			<li>
		// 				{`You can also subscribe to Payments from Subscription Details > Add-On Subscriptions section in your Veritable Portal once you have signed up.`}
		// 			</li>
		// 			{`You will receive an email confirming that you have successfully subscribed.`}
		// 		</ul>
		// 	),
		// },
		// {
		// 	key: 'a3',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `How can I add the credentials for my Payment Gateway?`,
		// 	answer: (
		// 		<p>
		// 			{`Once you have subscribed to the Payments Add-on, go to Admin > Settings > Payment Integration. Choose your payment gateway from the dropdown, add the required fields under the “Live Credentials” card, and click on "Save". If you need assistance adding the same, please contact our support team at`}
		// 			{getSupportEmail({ email })}
		// 			{'.'}
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a4',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `Which payment gateways are you integrated with?`,
		// 	answer: (
		// 		<p>
		// 			{`We are currently integrated with Stripe and Authorize.net payment gateways. However, if you wish to request integration with your payment gateway, kindly reach out to our team at`}
		// 			{getSupportEmail({ email })}
		// 			{'.'}
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a5',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `Can I test my payment gateway before going live?`,
		// 	answer: (
		// 		<p>
		// 			{`Yes, you can test your payment gateway if it supports a test mode. Go to Admin > Settings > Payment Integration and add the required fields in the “Test Credentials” card. Once you click on "Save", you will see the option to test payments. Click on the button to test payments for the payment gateway.`}
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a6',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `How can I send a payment request?`,
		// 	answer: (
		// 		<p>
		// 			{`There are two modes of sending payment requests:`}
		// 			<ul>
		// 				<li>
		// 					<strong>{`Quick Pay`}</strong>
		// 					<p>
		// 						{`Quick Pay mode allows you to send a generic payment link to your client. Simply visit the “Quick Pay” section under Payments and you can share the payment link via email or share the QR code with your client. `}
		// 					</p>
		// 				</li>

		// 				<li>
		// 					<strong>{`Create a Payment Request`}</strong>
		// 					<p>
		// 						{`This mode allows you to generate a payment request, with specific details about your client such as their name, payment amount, etc.,`}
		// 					</p>
		// 					<p>{`To create a payment request:`}</p>
		// 					<ul>
		// 						<li>{`Go to the “New Request” section under Payments. `}</li>
		// 						<li>{`Fill in the details of the client such as their name, payment amount, contact information and a small description of the payment request.`}</li>
		// 						<li>{`Once you have entered the details, click on “Generate Request”. Upon creation of the request, a modal will open up with options to share the payment request with your client.`}</li>
		// 						<li>{`You can either give the QR code to the client to scan or simply share the payment request link via email.`}</li>
		// 					</ul>
		// 				</li>
		// 			</ul>
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a7',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `How can I view all the payment submissions?`,
		// 	answer: (
		// 		<p>
		// 			{`You can view all the payment submissions in the “All Payments” screen under the Payments section.`}
		// 			<br />
		// 			{`You can view all the payment submissions that come in for both the live and test modes of your payment gateway. You can also search and filter the submissions by using the various options given above the table to select a specific payment submission.`}
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a8',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `How can I update my organization"s settings to be visible to my clients when they are paying?`,
		// 	answer: (
		// 		<p>
		// 			{`You can edit your organization"s settings, such as name, email address, and location address to be visible to your clients when they are paying. Go to Admin > Settings > Payment Integration and add/edit the same from the “Organization Settings” card. Click on "Save" to save the changes.`}
		// 			<br />
		// 			{`If you wish to add/edit your organization"s logo, please contact our team at`}
		// 			{getSupportEmail({ email })}
		// 			{'.'}
		// 		</p>
		// 	),
		// },
		// {
		// 	key: 'a9',
		// 	category: [FAQ_CATEGORIES.Addon.category],
		// 	question: `How can I cancel my subscription to Payments Add-on?`,
		// 	answer: (
		// 		<p>
		// 			{`Go to Subscription Details > Add-On Subscriptions and click on “Unsubscribe from Payments Add-on”. You will receive an email confirming you have unsubscribed. Once you cancel your subscription, you will no longer be able to send/receive payments from your Veritable portal. However, you will be able to view your previous payment submissions.`}
		// 		</p>
		// 	),
		// },
		{
			key: 's1',
			category: [FAQ_CATEGORIES.Support.category],
			question: 'How can I contact support for Veritable?',
			answer: (
				<p>
					{'You may write to us at'}
					{getSupportEmail({ email, label: email })}
					{'for any queries.'}
				</p>
			),
		},
		{
			key: 's2',
			category: [FAQ_CATEGORIES.Support.category],
			question: 'How can I provide feedback for Veritable?',
			answer: (
				<p>
					{'You can offer feedback for Veritable using the "Feedback” ribbon located at the right end of any Veritable screen.'}
				</p>
			),
		},
	];

	return _.map(allQueries, (query) => ({ ...query, answerString: reactNodeToString(query.answer) }));
};

export { CODE_EXPLORER_TUTORIAL_VIDEO, getSupportEmail, getAllQueries };
